import { hasAnyIntegrationPermissions } from '@admin/integrations/utils'
import EngagementIcon from '@assets/icons/EngagementIcon'
import GiftIcon from '@assets/icons/GiftIcon'
import IncentiveIcon from '@assets/icons/IncentiveIcon'
import IntegrationsIcon from '@assets/icons/IntegrationsIcon'
import PlatformSetupIcon from '@assets/icons/PlatformSetupIcon'
import ToolIcon from '@assets/icons/ToolIcon'
import AppPaths from '@globals/paths'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import { AppPermission } from '@shared/permissions'
import { T } from '@transifex/react'
import { NavItem } from '../types'

export const USERS_AND_TEAMS_KEY = 'usersAndTeams'

export const adminNavConfig: NavItem[] = [
  {
    type: 'link',
    name: <T _str="Dashboard" />,
    path: AppPaths.tenant.admin.dashboard(),
    icon: <TableChartOutlinedIcon />,
    key: 'dashboard',
    hideOnMobile: true,
    visible: hasAppPermission =>
      hasAppPermission('onboardingApp', AppPermission.OnboardingAppManage),
    pathMatch: [/^\/dashboard/],
  },
  {
    type: 'link',
    name: <T _str="Integrations" />,
    path: AppPaths.tenant.admin.integrations(),
    icon: <IntegrationsIcon />,
    hideOnMobile: true,
    key: 'integrations',
    visible: hasAnyIntegrationPermissions,
    pathMatch: [/^\/integrations(\/|$)/],
  },
  {
    type: 'group',
    icon: <GroupOutlinedIcon />,
    name: <T _str="User management" />,
    key: 'userManagement',
    children: [
      {
        type: 'link',
        name: <T _str="HRIS integration" />,
        path: AppPaths.tenant.admin.hrisIntegrations('records'),
        key: 'hrisIntegration',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
        pathMatch: [
          /^\/staffing\/import\/integrations/,
          /^\/staffing\/batch-add(\/|$)/,
          /^\/staffing(\/|$)/,
        ],
      },
      {
        type: 'link',
        name: <T _str="Users & teams" />,
        path: AppPaths.tenant.admin.members('users'),
        key: USERS_AND_TEAMS_KEY,
        mobileIcon: <GroupOutlinedIcon />,
        pathMatch: [
          // Note, this can be done in one RegEx, but we
          // split it apart for readability
          /^\/manage\/(teams|users|quick-teams)/,
          /^\/team(\/|$)/,
          /^\/user(\/|$)/,
        ],
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowse),
      },
      {
        type: 'link',
        name: <T _str="Security & access" />,
        path: AppPaths.tenant.admin.security(),
        key: 'securityAndAccess',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppManageAuthSettings),
        pathMatch: [/^\/tenant\/security/],
      },
      {
        type: 'link',
        name: <T _str="Delegation" />,
        path: AppPaths.tenant.admin.delegation(),
        hideOnMobile: true,
        key: 'delegation',
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppManageDelegation) ||
          hasAppPermission('identityApp', AppPermission.IdentityAppBrowseDelegators),
        pathMatch: [/^\/delegation(\/|$)/, /^\/delegation\/[^/]+\/edit/],
      },
    ],
  },
  {
    type: 'group',
    icon: <PlatformSetupIcon />,
    name: <T _str="Platform set up" />,
    key: 'platformSetup',
    children: [
      {
        type: 'link',
        name: <T _str="Branding" />,
        path: AppPaths.tenant.admin.customize(),
        key: 'branding',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppEditLogo) ||
          hasAppPermission('identityApp', AppPermission.IdentityAppEditBanner) ||
          hasAppPermission('identityApp', AppPermission.IdentityAppEditLocale),
        pathMatch: [/^\/customize/],
      },
      {
        type: 'link',
        name: <T _str="Recognition" />,
        path: AppPaths.tenant.admin.manageRecognition(),
        key: 'recognition',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('pointsApp', AppPermission.PointsAppViewPointsConfiguration) &&
          (hasAppPermission('recognitionApp', AppPermission.RecognitionAppManagePointPresets) ||
            hasAppPermission(
              'recognitionApp',
              AppPermission.RecognitionAppManageTenantPreferences,
            )),
        pathMatch: [/^\/recognition\/manage/],
      },
      {
        type: 'link',
        name: <T _str="Values & qualities" />,
        path: AppPaths.tenant.admin.values(),
        key: 'valuesAndQualities',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('valueApp', AppPermission.ValueAppCreateValue),
        pathMatch: [/^\/values(\/|$)/],
      },
      {
        type: 'link',
        name: <T _str="Language & time" />,
        path: AppPaths.tenant.admin.localization(),
        key: 'languageAndTime',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppEditLocale) ||
          hasAppPermission('valueApp', AppPermission.ValueAppTranslate) ||
          hasAppPermission('awardApp', AppPermission.AwardAppTranslate) ||
          hasAppPermission('incentiveApp', AppPermission.IncentiveAppTranslate),
        pathMatch: [/^\/localization/],
      },
      {
        type: 'link',
        name: <T _str="Notifications" />,
        path: AppPaths.tenant.admin.notifications(),
        key: 'notifications',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('recognitionApp', AppPermission.RecognitionAppManageTenantPreferences),
        pathMatch: [/^\/notifications$/],
      },
      {
        type: 'link',
        name: <T _str="Feature management" />,
        path: AppPaths.tenant.admin.featureManagement(),
        key: 'featureManagement',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('identityApp', AppPermission.IdentityAppEditTenantPreferences),
        pathMatch: [/^\/feature-management$/],
      },
    ],
  },
  {
    type: 'group',
    icon: <GiftIcon />,
    name: <T _str="Points & rewards" />,
    key: 'pointsAndRewards',
    children: [
      {
        type: 'link',
        name: <T _str="Points" />,
        path: AppPaths.tenant.admin.points('allocation'),
        key: 'points',
        hideOnMobile: true,
        visible: hasAppPermission => hasAppPermission('pointsApp', AppPermission.PointsAppBrowse),
        pathMatch: [/^\/points(\/|$)/],
      },
      {
        type: 'link',
        name: <T _str="Rewards" />,
        path: AppPaths.tenant.admin.rewards('setup'),
        key: 'rewards',
        hideOnMobile: true,
        pathMatch: [
          /^\/rewards\/setup/,
          /^\/rewards\/reporting/,
          /^\/rewards\/([^/]+)\/edit/,
          /^\/rewards\/configure(\/|$)/,
          /^\/rewards\/store(\/|$)/,
          /^\/rewards\/store\/[^/]+\/add-funds/,
        ],
        visible: hasAppPermission =>
          hasAppPermission('rewardsApp', AppPermission.RewardsAppAdmin) &&
          (hasAppPermission('rewardsApp', AppPermission.RewardsAppViewRedemptionHistory) ||
            hasAppPermission('pointsApp', AppPermission.PointsAppViewTransactions)),
      },
    ],
  },
  {
    type: 'group',
    icon: <EngagementIcon />,
    name: <T _str="Engagement" />,
    key: 'engagement',
    children: [
      {
        type: 'link',
        name: <T _str="Awards & nominations" />,
        path: AppPaths.tenant.admin.awards('awards'),
        key: 'awards',
        mobileIcon: <EmojiEventsOutlinedIcon />,
        mobilePathMatch: [/^\/awards\/list/],
        visible: hasAppPermission =>
          hasAppPermission('awardApp', AppPermission.AwardAppManage) ||
          hasAppPermission('awardApp', AppPermission.AwardAppNominationSender),
        pathMatch: [
          /^\/awards\/configure/,
          /^\/nominations\/configure/,
          /^\/awards\/list/,
          /^\/awards\/award/,
          /^\/awards\/nomination\/[^/]+/,
        ],
      },
      {
        type: 'link',
        name: <T _str="Incentives" />,
        path: AppPaths.tenant.admin.manageIncentives(),
        key: 'incentives',
        mobileIcon: <IncentiveIcon fill="inherit" />,
        visible: hasAppPermission =>
          hasAppPermission('incentiveApp', AppPermission.IncentiveAppReview),
        showUpgradeDialog: hasAppPermission =>
          hasAppPermission('incentiveApp', AppPermission.IncentiveAppUpgradeLicense),
        pathMatch: [
          /^\/incentives\/configure/,
          /^\/incentives\/manage/,
          /^\/incentives\/manage\/[^/]+/,
          /^\/incentives\/duplicate\/[^/]+/,
        ],
      },
      {
        type: 'link',
        name: <T _str="Announcements" />,
        path: AppPaths.tenant.admin.announcements(),
        key: 'announcements',
        mobileIcon: <CampaignOutlinedIcon />,
        visible: hasAppPermission =>
          hasAppPermission('announcementApp', AppPermission.AnnouncementAppManage),
        mobilePathMatch: [/^\/announcements\/configure/, /^\/announcements/],
        pathMatch: [/^\/announcements\/configure/, /^\/announcements/],
      },
      {
        type: 'link',
        name: <T _str="Spaces" />,
        path: AppPaths.tenant.admin.spacesManage('overview'),
        key: 'spaces',
        hideOnMobile: true,
        visible: hasAppPermission => hasAppPermission('spacesApp', AppPermission.SpacesAppManage),
        showUpgradeDialog: hasAppPermission =>
          hasAppPermission('spacesApp', AppPermission.SpacesAppUpgradeLicense),
        pathMatch: [/^\/spaces\/manage(\/overview|\/permission)?$/],
      },
      {
        type: 'link',
        name: <T _str="Check-ins" />,
        path: AppPaths.tenant.admin.checkInManage(),
        key: 'checkIns',
        hideOnMobile: true,
        visible: () => false,
        //visible: hasAppPermission => hasAppPermission('checkInApp', AppPermission.CheckInAppManage),
        // showUpgradeDialog: hasAppPermission =>
        //   hasAppPermission('checkInApp', AppPermission.CheckInAppUpgradeLicense),
        pathMatch: [/^\/check-ins\/manage$/, /^\/check-ins\/category\/[^/]+\/audience$/],
      },
      {
        type: 'link',
        name: <T _str="Celebrations" />,
        path: AppPaths.tenant.admin.configureCelebrations('privacy'),
        key: 'celebrations',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('celebrationApp', AppPermission.CelebrationAppEditTenantPreferences),
        pathMatch: [/^\/celebrations\/configure/],
      },
    ],
  },
  {
    type: 'group',
    icon: <ToolIcon />,
    name: <T _str="Admin tools" />,
    key: 'adminTools',
    children: [
      {
        type: 'link',
        name: <T _str="Flagged content" />,
        path: AppPaths.tenant.admin.flaggedContent('recognition'),
        key: 'flaggedContent',
        mobileIcon: <FlagOutlinedIcon />,
        visible: hasAppPermission =>
          hasAppPermission('recognitionApp', AppPermission.RecognitionAppModerate) ||
          hasAppPermission('commentApp', AppPermission.CommentAppModerate),
        pathMatch: [/^\/content\/flagged\//],
        mobilePathMatch: [/^\/content\/flagged\//],
      },
      {
        type: 'link',
        name: <T _str="Burnout insights" />,
        path: AppPaths.tenant.admin.analytics('insights'),
        key: 'burnoutInsights',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('checkInApp', AppPermission.CheckInAppViewAnalytics) ||
          hasAppPermission('metricApp', AppPermission.MetricAppBrowse),
        pathMatch: [/^\/analytics\//],
      },
      {
        type: 'link',
        name: <T _str="Security logs" />,
        path: AppPaths.tenant.admin.securityLogs(),
        key: 'securityLogs',
        hideOnMobile: true,
        visible: hasAppPermission =>
          hasAppPermission('auditingApp', AppPermission.AuditingAppBrowse),
        pathMatch: [/^\/security-logs$/],
      },
    ],
  },
  {
    type: 'link',
    name: <T _str="Reports" />,
    path: AppPaths.tenant.admin.allReports(),
    key: 'allReports',
    icon: <SummarizeOutlinedIcon />,
    visible: hasAppPermission => hasAppPermission('reportingApp', AppPermission.ReportingAppBrowse),
    pathMatch: [/^\/reports\//],
    mobilePathMatch: [
      /^\/reports\/all/,
      /^\/reports\/awardSummary/,

      // Note that we have an engagementActivityReport which should
      // be hidden on mobile, hence why we are adding the $ at the
      // end of this particular regex to ensure we show the
      // engagement summary page (/reports/engagement) in mobile
      // but not engagementActivity (/reports/engagementActivity)
      /^\/reports\/engagement$/,
      /^\/reports\/engagement\/share/,

      /^\/reports\/managerLeaderboard/,
      /^\/reports\/redemptionSummary/,
    ],
  },
  {
    type: 'link',
    name: <T _str="Logs" />,
    path: AppPaths.tenant.admin.logs(),
    hideOnMobile: true,
    icon: <ContentPasteOutlinedIcon />,
    key: 'logs',
    visible: () => false,
    // SYN-1296  visible: hasAppPermission => hasAppPermission('auditingApp', AppPermission.AuditingAppBrowse),
  },
]
